import { t } from "@lingui/macro";
import { ArrowRight } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";

import { Decoration } from "./decoration";

const HERO_IMAGE_SRC = "/screenshots/builder-dark.png";

export const HeroSection = () => {
  const isLoggedIn = useAuthStore((state) => !!state.user);

  return (
    <section id="hero" className="relative overflow-hidden">
      <Decoration.Grid />
      <Decoration.Gradient />

      <div
        className={`
          flex flex-col px-4 py-16
          sm:px-6 sm:py-20
          lg:flex-row lg:items-center lg:gap-x-10 lg:py-20 lg:pr-10
          lg:pl-[max(2.5rem,calc((100%-80rem)/2+2.5rem))]
          xl:gap-x-16 xl:py-24 xl:pr-16
        `}
      >
        {/* Text — fixed width, vertically centered against image */}
        <motion.div
          className="mx-auto max-w-xl shrink-0 text-center lg:mx-0 lg:w-[25rem] lg:max-w-none lg:text-left xl:w-[28rem]"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
        >
          <div className="flex justify-center lg:justify-start">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold leading-6 text-primary ring-1 ring-inset ring-primary/20">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              {t`AI-powered resume building`}
            </span>
          </div>

          <h1 className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl xl:text-5xl">
            {t`AI Resume Builder That Gets You Hired`}
          </h1>

          <p className="mt-5 text-[15px] leading-7 text-foreground/70">
            {t`Build ATS-optimized resumes and cover letters tailored to any job in minutes. AI writes your content, checks your ATS score, matches you to jobs, and generates cover letters, all in one builder. Start with a 3-day free trial.`}
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
            {isLoggedIn ? (
              <>
                <Button asChild size="lg" className="w-full text-base sm:w-auto">
                  <Link to="/dashboard">
                    {t`Go to Dashboard`}
                    <ArrowRight className="ml-2" weight="bold" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild className="w-full text-base sm:w-auto">
                  <a href="/auth/register">{t`Start 3-day trial`}</a>
                </Button>
              </>
            ) : (
              <>
                <Button asChild size="lg" className="w-full text-base sm:w-auto">
                  <Link to="/auth/register">
                    {t`Build Your Resume Now`}
                    <ArrowRight className="ml-2" weight="bold" />
                  </Link>
                </Button>
                <Button variant="link" size="lg" asChild className="text-base">
                  <a href="#ai-writer">{t`See AI Features`}</a>
                </Button>
              </>
            )}
          </div>

          <div className="mt-5 flex items-center justify-center gap-x-2 text-sm text-foreground/50 lg:justify-start">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="h-4 w-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span>{t`Trusted by 42,000+ professionals`}</span>
          </div>
        </motion.div>

        {/* Image — flex-1 fills ALL remaining width, properly padded from edge */}
        <motion.div
          className="mx-auto mt-12 w-full max-w-2xl lg:mx-0 lg:mt-0 lg:min-w-0 lg:max-w-3xl lg:flex-1 xl:max-w-4xl"
          viewport={{ once: true }}
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1, transition: { duration: 0.6, delay: 0.15 } }}
        >
          <img
            width={1920}
            height={1080}
            src={HERO_IMAGE_SRC}
            alt="CareerBldr AI Resume Builder for building ATS-optimized resumes with AI"
            className="w-full rounded-xl shadow-2xl ring-1 ring-foreground/10"
          />
        </motion.div>
      </div>
    </section>
  );
};
